import {
  createContext,
  useCallback,
  useMemo,
  useContext,
  useState,
} from "react";
import { useRouter } from "next/router";
import useSWR, { KeyedMutator } from "swr";
import { sdk } from "~lib";
import {
  OrderQuery,
  ReleaseType,
  UpdateOrderInput,
  UpdateOrderMutation,
  VenueSeating,
} from "~graphql/sdk";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import {
  ExtendedGraphQLError,
  getGraphQLError,
  handlePromise,
  showToast,
} from "~lib/helpers";
import { useAccount } from "~hooks/useAccount";
import { useOrganization } from "~hooks/useOrganization";
import {
  isTransactionError,
  isTransactionPending,
  isOrderCompleted,
  isPointsTransaction,
  pollOrder,
  getOrder,
  getPushOptions,
} from "../utils/order.helpers";
import { StripeConfigProvider } from "~lib/stripeConfigContext";
import { Url } from "url";

interface OrderContextProps {
  order: OrderQuery["order"];
  error: Error | null;
  isBackOfficeOrder: boolean;
  isPointsOrder: boolean;
  pointName: string;
  isPOSOrder: boolean;
  hasBuyerEmail: boolean;
  isCompleted: boolean;
  isProcessing: boolean;
  containsOnlyChildTickets: boolean;
  showProcessingPage: boolean;
  transactionError: boolean;
  redirectConfig?: Partial<Url>;
  canSpendPoints: boolean;
  canCreateHoldOrder: boolean;
  eventId?: string;
  membershipId?: string;
  enableAutoRenew: boolean;
  isSeated: boolean;
  methods: {
    mutateOrderQuery: KeyedMutator<OrderQuery["order"]>;
    updateOrder: (
      update: UpdateOrderInput
    ) => Promise<{
      data?: UpdateOrderMutation;
      error?: ExtendedGraphQLError;
    }>;
    restartOrderPolling?: () => void;
  };
}

export const OrderContext = createContext<OrderContextProps | undefined>(
  undefined
);

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};

export function OrderProvider({
  prefetchedOrder,
  children,
}: {
  prefetchedOrder?: OrderQuery["order"];
  children: React.ReactNode;
}) {
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const router = useRouter();
  const { isAdmin, isEventManager, isSalesOutlet, user } = useAccount();
  const { organization } = useOrganization();

  const [transactionError, setTransactionError] = useState(
    isTransactionError(prefetchedOrder)
  );

  const [shouldPoll, setShouldPoll] = useState<boolean>(
    isTransactionPending(prefetchedOrder)
  );

  const { data: order, error, mutate } = useSWR<OrderQuery["order"], Error>(
    router.query?.orderId
      ? ["order", organization.id, router.query.orderId]
      : null,
    async (_: string, orgId: string, orderId: string) => {
      const orderResult = await (shouldPoll
        ? pollOrder(orgId, orderId)
        : getOrder(orgId, orderId));

      if (isTransactionError(orderResult)) {
        setTransactionError(true);
        setShouldPoll(false);
      }

      if (isTransactionPending(orderResult)) {
        if (!shouldPoll) {
          setShouldPoll(true);
        }
      } else if (shouldPoll) {
        setShouldPoll(false);
      }

      return orderResult;
    },
    {
      shouldRetryOnError: true,
      // when the payment is initiated refresh every 5 seconds
      ...(shouldPoll && { refreshInterval: 5000 }),

      fallbackData: prefetchedOrder,
      revalidateOnMount: prefetchedOrder ? false : true,
    }
  );

  const isOutlet = isSalesOutlet && order?.release?.type === ReleaseType.Outlet;
  const isBackOfficeOrder = isAdmin || isEventManager || isOutlet;
  const isPointsOrder = isPointsTransaction(order);

  const canSpendPoints =
    (order?.membership?.canUsePoints || order?.event?.canUsePoints) && !!user;
  const canCreateHoldOrder =
    (isAdmin || isEventManager || order?.release?.allowHoldOrders) &&
    !isSalesOutlet;

  const isCompleted = isOrderCompleted(order);

  const redirectConfig = useMemo(() => getPushOptions(order), [order]);
  const hasBuyerEmail = !!order?.buyerInformation?.email;

  const isPOSOrder = !!order?.pos;

  const containsOnlyChildTickets =
    order?.lineItems?.edges?.length > 0 &&
    order?.lineItems?.edges?.every(({ node }) => {
      return node.ticketType?.isChild || node.membershipType?.isChild;
    });

  const isSeated =
    order?.event?.venue?.seating !== VenueSeating.NonSeated &&
    order?.membership?.venue?.seating !== VenueSeating.NonSeated;

  const updateOrder = useCallback(
    async (input: UpdateOrderInput) => {
      const sdkFn = await recaptchaSdkGenerator("updateOrder");
      const { data, error } = await handlePromise(async () =>
        sdkFn({ orgId: organization?.id }).updateOrder({
          id: order.id,
          input,
        })
      );

      if (error) {
        const e = getGraphQLError(error);
        // Don't show toast for status code 987 (unmet purchase restrictions)
        // This will be handled by the Purchase Restrictions Modal
        if (e.statusCode !== 987) {
          showToast(e.message, "error");
        }
      }

      void mutate(order, true);

      return {
        data,
        error: error ? getGraphQLError(error) : undefined,
      };
    },
    [order, organization?.id, mutate, recaptchaSdkGenerator]
  );

  return (
    <OrderContext.Provider
      value={{
        isBackOfficeOrder,
        isPointsOrder,
        canSpendPoints,
        canCreateHoldOrder,
        pointName: organization?.point?.name ?? "points",
        isPOSOrder,
        hasBuyerEmail,
        order,
        error,
        transactionError,
        isProcessing: shouldPoll,
        showProcessingPage: shouldPoll || transactionError,
        isCompleted,
        redirectConfig,
        eventId: order?.event?.id,
        membershipId: order?.membership?.id,
        containsOnlyChildTickets,
        enableAutoRenew: order?.membership?.enableAutoRenew || false,
        isSeated,
        methods: {
          mutateOrderQuery: mutate,
          updateOrder,
          restartOrderPolling: () => {
            window.location.reload();
          },
        },
      }}
    >
      <StripeConfigProvider config={order?.stripeConfig}>
        {children}
      </StripeConfigProvider>
    </OrderContext.Provider>
  );
}
